import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { docTypeIcon } from '../lib/docIcons';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import type { DocTreeNode, DocTreeRoot } from '../lib/docTree';

interface DocTreePanelProps {
  roots: DocTreeRoot[];
  /** Folder ids currently expanded. */
  expandedIds: ReadonlySet<string>;
  onToggleFolder: (id: string) => void;
  /** The open document's full path, for the active highlight. */
  activePath?: string | null;
  /** Called when a document link is followed (the drawer closes itself here). */
  onNavigate?: () => void;
}

/**
 * The documents file tree: literal folders as collapsible disclosures, files
 * as links. Deliberately NOT an ARIA `role="tree"` widget — that would require
 * roving-tabindex/arrow-key machinery; nested disclosure buttons with
 * `aria-expanded` (the JournalFolderList pattern) keep Tab/Enter/Escape
 * working and match every other collapsible surface in the app.
 */
export function DocTreePanel({
  roots,
  expandedIds,
  onToggleFolder,
  activePath,
  onNavigate,
}: DocTreePanelProps) {
  return (
    <div className="space-y-4">
      {roots.map((root) => (
        <section key={root.label} aria-label={root.label}>
          <p className="pb-1 px-3 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
            {root.label}
          </p>
          <NodeList
            nodes={root.children}
            nested={false}
            expandedIds={expandedIds}
            onToggleFolder={onToggleFolder}
            activePath={activePath}
            onNavigate={onNavigate}
          />
        </section>
      ))}
    </div>
  );
}

interface NodeListProps extends Omit<DocTreePanelProps, 'roots'> {
  nodes: DocTreeNode[];
  nested: boolean;
}

function NodeList({
  nodes,
  nested,
  expandedIds,
  onToggleFolder,
  activePath,
  onNavigate,
}: NodeListProps) {
  return (
    <ul
      className={
        nested ? 'space-y-0.5 ml-4 pl-1.5 border-l border-[var(--card-border)]' : 'space-y-0.5'
      }
    >
      {nodes.map((node) =>
        node.kind === 'folder' ? (
          <li key={node.id}>
            <button
              type="button"
              onClick={() => onToggleFolder(node.id)}
              aria-expanded={expandedIds.has(node.id)}
              className={`w-full min-h-10 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-[var(--text-primary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
            >
              <ChevronRight
                className={`w-4 h-4 text-[var(--text-tertiary)] shrink-0 transition-transform ${expandedIds.has(node.id) ? 'rotate-90' : ''}`}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">{node.name}</span>
            </button>
            {expandedIds.has(node.id) && (
              <NodeList
                nodes={node.children}
                nested
                expandedIds={expandedIds}
                onToggleFolder={onToggleFolder}
                activePath={activePath}
                onNavigate={onNavigate}
              />
            )}
          </li>
        ) : (
          <li key={node.id}>
            <DocLeaf
              name={node.name}
              path={node.path}
              type={node.type}
              active={node.path === activePath}
              onNavigate={onNavigate}
            />
          </li>
        )
      )}
    </ul>
  );
}

interface DocLeafProps {
  name: string;
  path: string;
  type: string | null;
  active: boolean;
  onNavigate?: () => void;
}

function DocLeaf({ name, path, type, active, onNavigate }: DocLeafProps) {
  const Icon = docTypeIcon(type);
  return (
    <Link
      to={`/docs/${path}`}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`min-h-10 flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${FOCUS_RING_CLASSES} ${
        active
          ? 'bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] text-white shadow-lg font-medium'
          : 'text-[var(--text-secondary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)]'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </Link>
  );
}
